/**
 * @lodex-pro/web-shared/auth/role-labels — human labels for role codes.
 *
 * The header badge (AppShell / AdminShell) shows the caller's permission level.
 * These map the raw `RoleCode` enum (packages/shared/src/auth/role-codes.ts) to a
 * human string so the badge never displays a raw `DISPATCH_MANAGER`-style value.
 */

// The SUBPATH, not the bare barrel. `packages/shared/src/index.ts` re-exports
// `./db/schema/index.js`, so a VALUE import of the barrel pulls drizzle-orm into
// every browser bundle that reaches this module — and since web-shared is in all
// four web apps, this one line put drizzle in all four. `auth/role-codes` exports
// both of these and imports no schema (Phase 18 / Task 18.9, which requires 0
// drizzle references in the web-portal bundle).
//
// web-dispatch and web-billing still have 11 and 1 barrel value-imports of their
// own and are NOT fixed by this; cleaning those up is its own queued task.
import { ROLE_CODES, type RoleCode } from '@lodex-pro/shared/auth/role-codes';

/** RoleCode → display label. Keyed by the canonical §6 role set (10 codes) +
 *  DISPOSAL_ADMIN (Phase 16 / Task 16.4 / DEC-161) + the four combined office
 *  roles (DEC-313). */
export const ROLE_DISPLAY_LABELS: Record<RoleCode, string> = {
  PLATFORM_ADMIN: 'Platform Admin',
  TENANT_ADMIN: 'Tenant Admin',
  DISPATCH_MANAGER: 'Dispatch Manager',
  DISPATCHER: 'Dispatcher',
  TRUCK_PUSHER: 'Truck Pusher',
  BILLING_MANAGER: 'Billing Manager',
  BILLING_STAFF: 'Billing Staff',
  DRIVER: 'Driver',
  OPERATOR_ADMIN: 'Operator Admin',
  FOREMAN: 'Foreman',
  DISPOSAL_ADMIN: 'Disposal Admin',
  // DEC-313 — combined office roles. Both halves are named in full: once all
  // four exist, "Dispatcher + Billing" would not say WHICH billing tier.
  DISPATCHER_BILLING_STAFF: 'Dispatcher + Billing Staff',
  DISPATCHER_BILLING_MANAGER: 'Dispatcher + Billing Manager',
  DISPATCH_MANAGER_BILLING_STAFF: 'Dispatch Manager + Billing Staff',
  DISPATCH_MANAGER_BILLING_MANAGER: 'Dispatch Manager + Billing Manager',
  PUMPER: 'Pumper',
};

const KNOWN_ROLES = new Set<string>(ROLE_CODES);

/** Title-case an unknown code (`SOME_ROLE` → `Some Role`) so the badge never shows a raw enum. */
function titleCase(code: string): string {
  return code
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Human label for a role code, or `null` when there is no role to show
 * (null/undefined/empty) — callers omit the role line in that case. Unknown
 * (non-empty) codes fall back to a title-cased form rather than the raw enum.
 */
export function roleDisplayLabel(role: string | null | undefined): string | null {
  if (role === null || role === undefined || role === '') return null;
  if (KNOWN_ROLES.has(role)) return ROLE_DISPLAY_LABELS[role as RoleCode];
  return titleCase(role);
}
